import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { logEvent } from '@/pages/api/v1/utils';
import {
  getGetApiV1WhitelistQueryKey,
  usePostApiV1Whitelist,
} from '@/open-api/whitelist/whitelist';

import type {
  YouTubeChannelRequest,
  YouTubeChannelSnippet,
} from '@/components/UBSafelist/types';

export const useWhitelistChannel = () => {
  const queryClient = useQueryClient();
  const { asPath } = useRouter();
  const queryKey = getGetApiV1WhitelistQueryKey();

  const mutation = usePostApiV1Whitelist({
    mutation: {
      onMutate: (variables) => {
        const newChannel = variables.data;

        void logEvent({
          event_type: 'safelist_channel',
          asPath,
          metadata: {
            channel_id: newChannel?.channelId?.toString() ?? '',
          },
        });
      },
      onSettled: async () => {
        // Clear errored state so the query can re-enable and refetch on the next render
        const cachedQuery = queryClient.getQueryState(queryKey);
        if (cachedQuery?.status === 'error') {
          queryClient.removeQueries({ queryKey });
        }
        await queryClient.invalidateQueries({ queryKey });
      },
    },
  });

  const whitelistChannel = (
    channel: YouTubeChannelRequest | YouTubeChannelSnippet
  ) =>
    mutation.mutateAsync({
      data: {
        channelId: channel.channelId,
        channelName: channel.title ?? channel.channelTitle ?? '',
      },
    });

  return {
    whitelistChannel,
    isWhitelisting: mutation.isPending,
    error: mutation.error
      ? 'Something went wrong while safelisting your channel.'
      : undefined,
  };
};
